import React, { useEffect, useState, useRef } from 'react';
import { checkIfStuck } from '../../../utils';
import Icon from '../../Icon/Icon';
import Chevron from '../../Chevron/Chevron';
import { useGlobalValue } from '../../../state/GlobalProvider';
import Dropdown from '../../inputs/Dropdown/Dropdown';
import SearchInput from '../../inputs/SearchInput/SearchInput';
import Spinner from '../../Spinner/Spinner';
import { Link } from 'react-router-dom';
import DropdownIcon from '../../DropdownIcon/DropdownIcon';
import { checkRoles } from '../../../utils/roleValidator';
import styles from './Header.module.sass';
import MobileMenu from '../AsideMenu/MobileMenu';
import { UserRoles } from '../../../utils/userRoles';
import HeaderMessage from '../HeaderMessage/HeaderMessage';

const renderActions = (reportsAction, downloadableQueries, download, downloading, locale, pushDialog, i18n, totalLinesCsv) => {
  const actions = [];

  if (reportsAction && downloadableQueries && downloadableQueries.length) {
    if (downloadableQueries.length === 1) {
      actions.push( /*#__PURE__*/React.createElement("button", {
        key: "download",
        className: `${styles['action-button']} ${downloading ? styles['has-activity'] : ''}`,
        onClick: () => {
          download && download(downloadableQueries, locale);
        }
      }, /*#__PURE__*/React.createElement(Icon, {
        name: "csv"
      }), downloading && /*#__PURE__*/React.createElement(Spinner, {
        className: styles['spinner']
      })));
    } else {
      actions.push( /*#__PURE__*/React.createElement("button", {
        key: "download",
        className: `${styles['action-button']} ${downloading ? styles['has-activity'] : ''}`,
        onClick: () => {
          !downloading && pushDialog('MultipleDownloadDialog', i18n`Please choose one of the following options`, {
            queries: downloadableQueries,
            download,
            locale,
            totalLinesCsv
          }).then(() => {}).catch(() => null);
        }
      }, /*#__PURE__*/React.createElement(Icon, {
        name: "csv"
      }), downloading && /*#__PURE__*/React.createElement(Spinner, {
        className: styles['spinner']
      })));
    }
  }

  return actions;
};

const renderFiscalYearSelector = (fiscalYears, fiscalYear, i18n, onChange) => {
  const options = (fiscalYears || []).map(p => ({
    id: `${p}`,
    name: p.toString()
  }));
  const currentValue = fiscalYear === 'current' ? options[0] : null;
  const value = fiscalYear ? options.find(({
    id
  }) => id === fiscalYear) || currentValue : options[0];
  return /*#__PURE__*/React.createElement(Dropdown, {
    className: styles['period-dropdown'],
    secondary: true,
    options: options || [],
    value: value,
    onChange: onChange,
    i18n: i18n
  });
};

const renderProfileSelector = (myProfileOptions, i18n, onChange) => {
  return /*#__PURE__*/React.createElement(DropdownIcon, {
    style: {
      paddingLeft: '10px'
    },
    onChange: onChange,
    options: myProfileOptions,
    i18n: i18n
  });
};

const formatUserName = name => {
  const userName = name.split(" ");
  return userName && userName.length && userName.length < 3 ? name : `${userName[0]} ${userName[userName.length - 1]}`;
};

const Header = props => {
  const {
    className,
    searchAction,
    reportsAction,
    settingsAction,
    title,
    fiscalYears,
    project,
    downloadableQueries = [],
    items = [],
    search = {},
    downloading,
    menuLogo,
    menuTitle,
    menuSubtitle,
    organizations,
    navigation,
    hideMyProfile = false,
    displayInfo = "",
    headerMessage = {},
    displaySupportIcon = false,
    totalLinesCsv
  } = props;
  const {
    user,
    params,
    i18n,
    goto,
    locale,
    download,
    repositoryStatus,
    reprocessRepository,
    pushDialog,
    logout,
    history
  } = useGlobalValue();
  const myProfileOptions = [{
    id: 'reset_password',
    name: 'Change Password'
  }, {
    id: 'logout',
    name: 'Logout'
  }];
  const {
    projectId,
    fiscalYear
  } = params;
  const [stuck, setStuck] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const elem = useRef(null);

  const handleScroll = () => {
    const isStuck = checkIfStuck(elem.current, 'top');

    if (isStuck !== stuck) {
      setStuck(isStuck);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  });
  const titleArray = Array.isArray(title) ? [...title] : [title];
  const lastPart = titleArray.pop();
  const linkTo = `/${locale}/${projectId}/repository/folders/${fiscalYear}`;

  const handleReprocessRepository = () => {
    pushDialog('ConfirmDialog', i18n`Process All`, {
      title: i18n`Are you sure you want to process repository?`,
      text: i18n`Please confirm that you want to proceed!`
    }).then(() => {
      reprocessRepository({
        fiscalYear: fiscalYear,
        projectId: projectId,
        userId: user.id
      }).then(() => {}).catch(() => null);
      goto && goto(`/${projectId}/repository/activity/status`);
    }).catch(() => null);
  };

  const handleToggleMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, repositoryStatus && !repositoryStatus.status && /*#__PURE__*/React.createElement("div", {
    className: styles['alert-process-repository']
  }, /*#__PURE__*/React.createElement("p", null, i18n`Inconsistent Repository! Please, process all the files.`), /*#__PURE__*/React.createElement("div", {
    className: styles['btns-actions']
  }, checkRoles(user, [UserRoles.ADMIN, UserRoles.OWNER, UserRoles.WRITER, UserRoles.PP_SUPPORT, UserRoles.SUPPORT]) ? /*#__PURE__*/React.createElement("small", {
    onClick: () => {
      handleReprocessRepository();
    }
  }, /*#__PURE__*/React.createElement("strong", null, i18n`Process Repository`)) : /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement(Link, {
    className: styles['link'],
    to: linkTo
  }, /*#__PURE__*/React.createElement("small", null, /*#__PURE__*/React.createElement("strong", null, i18n`See Repository`)), /*#__PURE__*/React.createElement(Chevron, {
    direction: "right"
  })))), Object.keys(headerMessage).length !== 0 && /*#__PURE__*/React.createElement(HeaderMessage, {
    headerMessage: headerMessage
  }), /*#__PURE__*/React.createElement("div", {
    className: styles['tracker'],
    ref: elem
  }), /*#__PURE__*/React.createElement("div", {
    className: `${styles['root']} ${className || ''} ${stuck ? styles['is-stuck'] || '' : ''}`
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['left-actions']
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['mobile-only']
  }, /*#__PURE__*/React.createElement("button", {
    className: styles['mobile-button'],
    onClick: handleToggleMenu
  }, /*#__PURE__*/React.createElement("span", null))), /*#__PURE__*/React.createElement("div", {
    className: styles['breadcrumb']
  }, titleArray && titleArray.length ? /*#__PURE__*/React.createElement("small", {
    className: styles['small-text']
  }, titleArray.join(' | ')) : null, lastPart && /*#__PURE__*/React.createElement("h5", {
    className: styles['title-header']
  }, lastPart)), /*#__PURE__*/React.createElement("div", {
    className: styles['breadcrumb']
  }, displayInfo && displayInfo.length > 0 && /*#__PURE__*/React.createElement(Icon, {
    name: "info",
    className: styles['informative-icon'],
    iconPlaceholder: `${displayInfo}`
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles['right-actions']
  }, searchAction && /*#__PURE__*/React.createElement(SearchInput, {
    className: styles['search-input'],
    items: items,
    search: (search || {}).result || {}
  }), reportsAction && downloadableQueries.length ? /*#__PURE__*/React.createElement("div", {
    className: styles['global-actions']
  }, renderActions(reportsAction, downloadableQueries, download, downloading, locale, pushDialog, i18n, totalLinesCsv)) : null, project && project.taxRegistrationNumber && /*#__PURE__*/React.createElement("div", {
    className: styles['tax-registration-number']
  }, /*#__PURE__*/React.createElement("small", null, i18n`TRN: ${project.taxRegistrationNumber}`)), params.fiscalYear && fiscalYears && fiscalYears.length ? /*#__PURE__*/React.createElement("div", null, renderFiscalYearSelector(fiscalYears, params.fiscalYear, i18n, e => {
    history.push(history.location.pathname.replace(/^(\/(pt|lt|en|es)\/\d+\/)(\d{4}|current)(\/*.*)/, `$1${e.target.value.id}$4`));
  })) : null, hideMyProfile === false ? /*#__PURE__*/React.createElement("div", {
    className: styles['user-header-actions']
  }, displaySupportIcon ? /*#__PURE__*/React.createElement("button", {
    key: "freshDeskIcon",
    className: styles['action-button'],
    onClick: () => {
      window.FreshworksWidget('open');
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "freshdesk-support",
    className: styles['support-icon']
  })) : null, /*#__PURE__*/React.createElement("div", {
    className: styles['user-header-details']
  }, renderProfileSelector(myProfileOptions, i18n, e => {
    if (e.target.value.id === 'logout') {
      logout();
    } else {
      goto && goto(`/my_profile/${e.target.value.id}`);
    }
  }), user && user.name ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", null, i18n`Welcome, `), /*#__PURE__*/React.createElement("p", {
    style: {
      fontWeight: 'bold'
    }
  }, formatUserName(user.name))) : null)) : null)), mobileMenuOpen && /*#__PURE__*/React.createElement(MobileMenu, {
    logo: menuLogo,
    title: menuTitle,
    subtitle: menuSubtitle,
    organizations: organizations,
    project: project,
    navigation: navigation,
    closeMenu: () => {
      setMobileMenuOpen(false);
    },
    settingsAction: settingsAction
  }));
};

export default Header;